import React, { useState, useMemo, useEffect } from 'react';
import { MaterialReactTable, MRT_ColumnDef, MRT_Row, MRT_TableInstance, MRT_TableOptions, useMaterialReactTable } from 'material-react-table';
import { IconButton, Box, Tooltip, DialogContent, DialogTitle, Dialog, createTheme, ThemeProvider } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IMember } from '../../interfaces/Member.interface';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../../contexts/LoadingContext';
import { useHeader } from '../../contexts/HeaderContext';
import { useMembers } from '../../contexts/MemberContext';
import { EditMemberForm } from './MemberDialogs/EditMember';
import CloseIcon from '@mui/icons-material/Close';
import { useFetch } from '../../utils/FetchTemplate';
import PageviewIcon from '@mui/icons-material/Pageview';
import { set } from 'react-hook-form';
import { useBookings } from '../../contexts/BookingContext';
import { BookingHistory } from '../../components/BookingHistory';
import { IBooking } from '../../interfaces/Booking.interface';
import { useAuth } from '../../contexts/AuthContext';
import DialogWithClose from '../../components/DialogWithClose';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import AddCardIcon from '@mui/icons-material/AddCard';
import AddServiceToMemberDialog from './MemberDialogs/AddServiceToMemberDialog';
import { useSetting } from '../../contexts/SettingContext';

const MembersTable: React.FC = () => {
  const navigate = useNavigate();
  const { header, setHeader } = useHeader();
  const [isSelecting, setIsSelecting] = useState(false);
  const { members, fetchMembers } = useMembers();
  const [isEditing, setIsEditing] = useState(false);
  const { postData, getData } = useFetch()
  const [isViewing, setIsViewing] = useState(false);
  const [currentBookings, setCurrentBookings] = useState<IBooking[]>([]);
  const { bookings, fetchBookings } = useBookings();
  const { loggedIn } = useAuth();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<IMember | null>(null);
  const [openAddService, setOpenAddService] = useState(false);
  const [targetMember, setTargetMember] = useState<IMember | null>(null);
  const { settings } = useSetting();
  const handleDelete = async (memberId: string, uid: string) => {
    try {
      await postData('/api/member/delete', { memberId });
      //await postData('/api/auth/deleteUser', { userId: uid });
      setOpenDeleteDialog(false);
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      fetchMembers();
    }
  };

  useEffect(() => {
      setHeader("Membership")
      fetchMembers();
  }, []);


  const columns: Array<MRT_ColumnDef<IMember>> = useMemo(
    () => [
      {
        header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <Tooltip title="Edit">
              <IconButton onClick={() => {
                table.setEditingRow(row)
                setIsEditing(true);
              }}>
                <EditIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="View History">
              <IconButton onClick={() => openViewHistoryModal(row)}>
                <PageviewIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Add Service">
              <IconButton onClick={() => { setTargetMember(row.original); setOpenAddService(true); }}>
                <AddCardIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
              
              

          </Box>
        ),
        size: 150, // ensure enough space for action buttons
        minSize: 120,
        maxSize: 200,
      },
      { 
        accessorKey: 'firstName', 
        header: 'First Name', 
        muiEditTextFieldProps: { required: true },
        accessorFn: (row) => row?.firstName || ''
      },
      { 
        accessorKey: 'lastName', 
        header: 'Last Name', 
        muiEditTextFieldProps: { required: true },
        accessorFn: (row) => row?.lastName || ''
      },
      { 
        accessorKey: 'role', 
        header: 'Role', 
        muiEditTextFieldProps: { required: true },
        accessorFn: (row) => row?.role || ''
      },
      {
        accessorKey: 'email', 
        header: 'Email',
        muiEditTextFieldProps: {
          required: true,
          type: 'email',
        },
        accessorFn: (row) => row?.email || ''
      },
      { 
        accessorKey: 'phone', 
        header: 'Phone', 
        muiEditTextFieldProps: { required: true, type: 'tel' },
        accessorFn: (row) => row?.phone || ''
      },
      {
        accessorKey: 'wechatId',
        header: 'Wechat ID',
        enableEditing: false,
        accessorFn: (row) => row?.wechatId || ''
      },
      { 
        accessorKey: 'note', 
        header: 'Note',
        accessorFn: (row) => row?.note || ''
      },
      {
        header: 'Services',
        id: 'services',
        Cell: ({ row }) => {
          const ids = row.original.enrolledServiceIds || [];
          const names = ids.map(id => settings.services.find(s => s.serviceId === id)?.name).filter(Boolean) as string[];
          return <span>{names.length > 0 ? names.join(', ') : '-'}</span>;
        }
      },
    ],
    [members, settings.services]
  );

  //DELETE action
  const openDeleteConfirmModal = (row: MRT_Row<IMember>) => {
    setOpenDeleteDialog(true);
    setRowToDelete(row.original);
  };

  //VIEW action
  const openViewHistoryModal = (row: MRT_Row<IMember>) => {
    setIsViewing(true);
    const onClose = () => setIsViewing(false);
    const currentBooking = bookings.filter((booking) => booking.studentIds?.includes(row.original.memberId));
    setCurrentBookings(currentBooking);
  };

  const handleUpdateBooking = (event: IBooking) => {
    postData<IBooking>('/api/booking/update', event).then(() => {
      fetchBookings()
    }).catch().finally(() => {
    })
  }

  const table = useMaterialReactTable({
    columns,
    data: members || [],
    initialState: { 
      pagination: { pageIndex: 0, pageSize: 10 },
      sorting: members.length > 0 ? [{ id: 'lastName', desc: false }] : []
    },
    createDisplayMode: 'modal',
    editDisplayMode: 'modal',
    // enableColumnResizing: true,
    enableColumnActions: false,
    // enableExpanding: true,
    muiTableBodyCellProps: () => {
      // set font to 18
      return {
        sx: {
          fontSize: 16,
        },
      }
    },
    muiTableHeadCellProps: () => {
      // set font to 18
      return {
        sx: {
          fontSize: 18,
        },
      }
    },
    renderEditRowDialogContent: ({ row, table }) => {
      const handleFinishEdit = async () => {
        fetchMembers();
        table.setEditingRow(null); //exit editing mode
        setIsEditing(false);
      };
      return (
        <DialogWithClose maxWidth={'xl'} open={isEditing} onClose={() => {
          table.setEditingRow(null); //exit editing mode
          setIsEditing(false);
        }}>
          <DialogTitle>Edit Student</DialogTitle>
          <DialogContent>
            <EditMemberForm handleFinish={handleFinishEdit} prevData={row.original} />
          </DialogContent>
        </DialogWithClose>

      )
    },

    muiTableBodyRowProps: ({ row, table }) => {
      const handleMouseDown = () => {
        setIsSelecting(false); // Reset selection flag on mouse down
      };

      const handleMouseUp = (event: React.MouseEvent<HTMLTableRowElement>) => {
        if (isSelecting || event.button === 2) {
          return; // Do nothing if text was selected
        }

        // If the click event is on a button, prevent row click
        if ((event.target as HTMLElement).closest('button')) {
          event.stopPropagation();
          return;
        }

        // Navigate to the detail page if no selection
        // navigate('/member/detail', { state: { member: row.original } });
        table.setEditingRow(row)
                setIsEditing(true);
      };

      const handleMouseMove = () => {
        if (window.getSelection()?.toString()) {
          setIsSelecting(true); // Set flag if text is selected
        }
      };

      return {
        onMouseDown: handleMouseDown,
        onMouseUp: handleMouseUp,
        onMouseMove: handleMouseMove,
      };
    },
  });

  return (
    <div>
      <MaterialReactTable table={table} />
      <DialogWithClose open={isViewing} onClose={() => setIsViewing(false)}>
        <DialogTitle >
          Booking History
        </DialogTitle>
        
        <DialogContent >
          <BookingHistory
            data={currentBookings}
          />
        </DialogContent>
      </DialogWithClose>
      <ConfirmDeleteDialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)} 
        onConfirm={() => { handleDelete(rowToDelete?.memberId ?? "", rowToDelete?.uid ?? "") }} 
        itemName="member" 
      />
      <AddServiceToMemberDialog
        open={openAddService}
        member={targetMember}
        onClose={() => setOpenAddService(false)}
        onSuccess={() => { fetchMembers(); }}
      />
    </div>
  );
}
  ;

export default MembersTable;
